import { useState } from "react";
import { type CarrierTask, useRemoveTasksFromTrip } from "@/api/carrier";
import { Button } from "@/components/ui/button";
import { taskLabel } from "@/lib/task-reference";

/** Persistent collection work, independent of the route's date/window filters. */
export function CollectionExceptions({ tasks }: { tasks: CarrierTask[] }) {
  const remove = useRemoveTasksFromTrip();
  const [feedback, setFeedback] = useState("");
  const exceptions = [
    ...new Map(
      tasks
        .filter((task) => task.collectionException)
        .map((task) => [task.id, task])
    ).values(),
  ];
  if (exceptions.length === 0) return null;
  return (
    <section
      aria-label="Urgent collection exceptions"
      className="max-h-48 shrink-0 overflow-y-auto border-b border-amber-300 bg-amber-50 px-4 py-3"
    >
      <h2 className="text-sm font-semibold">
        Missing at collection — dispatcher action needed ({exceptions.length})
      </h2>
      <p className="text-xs">
        Confirm the parcel was found before delivery, or return its Task to
        unassigned for investigation and replanning.
      </p>
      {exceptions.map((task) => (
        <div
          key={task.id}
          className="mt-2 flex flex-wrap items-center justify-between gap-2 text-sm"
        >
          <span>
            {taskLabel(task)}
            {" · "}
            {task.tripId
              ? "Blocks Trip completion"
              : "Unassigned — collection still unresolved"}
          </span>
          {task.tripId && (
            <Button
              size="sm-touch"
              variant="outline"
              disabled={remove.isPending}
              aria-label={`Return ${taskLabel(task)} to unassigned`}
              onClick={() => {
                if (!task.tripId) return;
                setFeedback("");
                remove.mutate(
                  { tripId: task.tripId, taskIds: [task.id] },
                  {
                    onSuccess: () =>
                      setFeedback(
                        `${taskLabel(task)} returned to unassigned. Collection remains unresolved.`
                      ),
                    onError: () =>
                      setFeedback(
                        `${taskLabel(task)} could not be returned to unassigned. Retry.`
                      ),
                  }
                );
              }}
            >
              Return to unassigned
            </Button>
          )}
        </div>
      ))}
      {feedback && (
        <p role="status" className="mt-2 text-xs">
          {feedback}
        </p>
      )}
    </section>
  );
}
